import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  getSocketManager,
  type WsConnectionState,
  type WsMessageHandler,
} from '@/lib/socketManager';

export function useSocketState(): WsConnectionState {
  const manager = getSocketManager();
  return useSyncExternalStore(
    (callback) => manager.onStateChange(callback),
    () => manager.state,
  );
}

export function useSocketConnection(): WsConnectionState {
  const manager = getSocketManager();

  useEffect(() => {
    manager.connect();
  }, [manager]);

  return useSocketState();
}

/**
 * Reconnect the live socket whenever the active organization changes.
 * The server binds each WS connection's org at handshake time (see
 * `ws-broadcast.service.ts`'s `WsConnectionMeta.organizationId`) and
 * filters every broadcast through `matchesOrg`. So if the user switches
 * orgs but the socket stays open, broadcasts for the new org are
 * silently dropped — the exact webhook-gap symptom this fix targets.
 *
 * Mounted by the host `AppShell` alongside `useSocketConnection`; the
 * initial mount is a no-op so it doesn't race the first `connect()`.
 */
export function useSocketOrgReconnect(activeOrganizationId: string | null): void {
  const manager = getSocketManager();
  const connectedOrgRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (connectedOrgRef.current === undefined) {
      connectedOrgRef.current = activeOrganizationId;
      return;
    }
    if (connectedOrgRef.current !== activeOrganizationId) {
      connectedOrgRef.current = activeOrganizationId;
      manager.reconnect();
    }
  }, [manager, activeOrganizationId]);
}

export function useSocketMessage(handler: WsMessageHandler): void {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const manager = getSocketManager();
    return manager.onMessage((msg) => handlerRef.current(msg));
  }, []);
}

export function useSocketSubscription(streamId: string | null, afterSequence?: number): void {
  useEffect(() => {
    if (!streamId) return;
    const manager = getSocketManager();
    manager.subscribe(streamId, afterSequence);
    return () => manager.unsubscribe(streamId);
  }, [streamId, afterSequence]);
}
